import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { GatsbyImage, getSrc, IGatsbyImageData } from 'gatsby-plugin-image'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import ArrowLeft from 'src/images/svgs/icons/ArrowLeft.svg'
import ArrowRight from 'src/images/svgs/icons/ArrowRight.svg'
import * as GlobalStyles from 'src/styles/global.module.scss'
import * as Styles from './chickenFingerCalculator.module.scss'

export const CHICKEN_FINGER_CALCULATOR_CATEGORIES = [
  'Tailgates',
  'Extras',
  'Combos',
] as const

const CATEGORY_LABELS: Record<ChickenFingerCalculatorCategory, string> = {
  Tailgates: 'Tailgates',
  Combos: 'Combos',
  Extras: 'Extras & Drinks',
}

const DEFAULT_PARTY_SIZE_STEP = 5
const SLIDER_THUMB_SIZE = 44

const getPartySizeOptions = (
  minimum: number,
  maximum: number,
  increment: number
) => {
  const step = increment > 0 ? increment : DEFAULT_PARTY_SIZE_STEP
  const options = minimum > 0 ? [0, minimum] : [0]
  let nextValue = Math.ceil(minimum / step) * step

  if (options.includes(nextValue)) {
    nextValue += step
  }

  while (nextValue <= maximum) {
    options.push(nextValue)
    nextValue += step
  }

  if (options[options.length - 1] !== maximum) {
    options.push(maximum)
  }

  return options
}

export type ChickenFingerCalculatorCategory =
  (typeof CHICKEN_FINGER_CALCULATOR_CATEGORIES)[number]

export enum ChickenFingerCalculatorQuantityMode {
  fixedQuantity = 'Fixed Quantity',
  onePerPerson = 'One Per Person',
  byServingCapacity = 'By Serving Capacity',
}

export type ChickenFingerCalculatorRule = {
  category: ChickenFingerCalculatorCategory
  itemName: string
  itemDescription?: string
  itemImage?: IGatsbyImageData
  itemImageAlt?: string
  applicablePeopleMin: number
  applicablePeopleMax?: number
  servingCapacity?: number
  quantityMode: ChickenFingerCalculatorQuantityMode
  fixedQuantity?: number
  resultLabel?: string
}

export type ChickenFingerCalculatorCta = {
  text?: string
  link?: PrismicLinkType
}

export type ChickenFingerCalculatorCategoryDescriptions = Partial<
  Record<ChickenFingerCalculatorCategory, string>
>

export enum ChickenFingerCalculatorBackgroundColor {
  red = 'red',
  black = 'black',
  white = 'white',
}

export type ChickenFingerCalculatorContent = {
  anchor?: string
  backgroundColor?: ChickenFingerCalculatorBackgroundColor
  eyebrowText?: string
  headline?: string
  description?: string
  inputLabel?: string
  inputPlaceholder?: string
  minimumPeople?: number
  maximumPeople?: number
  sliderIncrement?: number
  invalidInputMessage?: string
  noResultsMessage?: string
  categoryDescriptions?: ChickenFingerCalculatorCategoryDescriptions
  orderCta?: ChickenFingerCalculatorCta
  rules: ChickenFingerCalculatorRule[]
}

export type ChickenFingerCalculatorRecommendation =
  ChickenFingerCalculatorRule & {
    quantity: number
  }

export const getChickenFingerCalculatorRecommendations = (
  partySize: number,
  rules: ChickenFingerCalculatorRule[]
): ChickenFingerCalculatorRecommendation[] =>
  rules
    .filter(rule => {
      const maximum = rule.applicablePeopleMax ?? Number.POSITIVE_INFINITY
      return partySize >= rule.applicablePeopleMin && partySize <= maximum
    })
    .map(rule => {
      let quantity = rule.fixedQuantity ?? 1

      if (
        rule.quantityMode === ChickenFingerCalculatorQuantityMode.onePerPerson
      ) {
        quantity = partySize
      }

      if (
        rule.quantityMode ===
          ChickenFingerCalculatorQuantityMode.byServingCapacity &&
        rule.servingCapacity &&
        rule.servingCapacity > 0
      ) {
        quantity = Math.ceil(partySize / rule.servingCapacity)
      }

      return { ...rule, quantity }
    })

type Props = {
  content: ChickenFingerCalculatorContent
}

const ChickenFingerCalculator = ({ content }: Props) => {
  const {
    backgroundColor = ChickenFingerCalculatorBackgroundColor.red,
    eyebrowText = '',
    headline = 'How many people are you feeding?',
    description = '',
    inputLabel = 'Number of people',
    minimumPeople = 1,
    maximumPeople = 500,
    sliderIncrement = DEFAULT_PARTY_SIZE_STEP,
    noResultsMessage = 'No recommendation is available for this party size.',
    categoryDescriptions = {},
    orderCta,
    rules,
  } = content
  const generatedAnchor = generateRandomId()
  const anchor = content.anchor ?? generatedAnchor
  const [partySize, setPartySize] = useState(0)
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const resultsRef = useRef<HTMLDivElement>(null)
  const partySizeOptions = getPartySizeOptions(
    minimumPeople,
    maximumPeople,
    sliderIncrement
  )
  const partySizeIndex = partySizeOptions.indexOf(partySize)
  const inputId = `${anchor}-party-size`
  const recommendations = getChickenFingerCalculatorRecommendations(
    partySize,
    rules
  )
  const sliderProgressRatio =
    partySizeOptions.length === 1
      ? 0.5
      : partySizeIndex / (partySizeOptions.length - 1)
  const sliderProgress = sliderProgressRatio * 100
  const sliderThumbOffset = (0.5 - sliderProgressRatio) * SLIDER_THUMB_SIZE
  const backgroundClass = {
    [ChickenFingerCalculatorBackgroundColor.red]: Styles.redBackground,
    [ChickenFingerCalculatorBackgroundColor.black]: Styles.blackBackground,
    [ChickenFingerCalculatorBackgroundColor.white]: Styles.whiteBackground,
  }[backgroundColor]

  useEffect(() => {
    const imageSources = new Set(
      rules.flatMap(rule => {
        const source = rule.itemImage && getSrc(rule.itemImage)
        return source ? [source] : []
      })
    )

    imageSources.forEach(source => {
      const image = new Image()
      image.src = source
    })
  }, [rules])

  useEffect(() => {
    const results = resultsRef.current
    if (!results) return undefined

    const handleCategoryScroll = () => {
      const categoryElements = Array.from(results.children) as HTMLElement[]
      const closestCategoryIndex = categoryElements.reduce(
        (closestIndex, category, categoryIndex) =>
          Math.abs(category.offsetLeft - results.scrollLeft) <
          Math.abs(
            categoryElements[closestIndex].offsetLeft - results.scrollLeft
          )
            ? categoryIndex
            : closestIndex,
        0
      )

      setActiveCategoryIndex(closestCategoryIndex)
    }

    results.addEventListener('scroll', handleCategoryScroll, { passive: true })
    return () => results.removeEventListener('scroll', handleCategoryScroll)
  }, [])

  const goToCategory = (categoryIndex: number) => {
    const results = resultsRef.current
    const category = results?.children[categoryIndex] as HTMLElement | undefined
    if (!results || !category) return

    results.scrollTo({ left: category.offsetLeft })
    setActiveCategoryIndex(categoryIndex)
  }

  const changeCategory = (direction: -1 | 1) => {
    const nextCategoryIndex = Math.min(
      CHICKEN_FINGER_CALCULATOR_CATEGORIES.length - 1,
      Math.max(0, activeCategoryIndex + direction)
    )
    goToCategory(nextCategoryIndex)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPartySize(partySizeOptions[Number(event.target.value)])
  }

  const changePartySize = (direction: -1 | 1) => {
    const nextIndex = Math.min(
      partySizeOptions.length - 1,
      Math.max(0, partySizeIndex + direction)
    )
    setPartySize(partySizeOptions[nextIndex])
  }

  return (
    <section
      id={anchor}
      className={`${Styles.chickenFingerCalculator} ${backgroundClass}`}
    >
      <div className={Styles.headingContainer}>
        {eyebrowText && <p className={Styles.eyebrow}>{eyebrowText}</p>}
        <h2>{headline}</h2>
        <div className={Styles.inputContainer}>
          <label className={Styles.srOnly} htmlFor={inputId}>
            {inputLabel}
          </label>
          <div className={Styles.sliderControl}>
            <button
              type="button"
              aria-label={`Decrease ${inputLabel}`}
              disabled={partySizeIndex === 0}
              onClick={() => changePartySize(-1)}
            >
              −
            </button>
            <div
              className={Styles.slider}
              style={
                {
                  '--slider-progress': `${sliderProgress}%`,
                  '--slider-thumb-offset': `${sliderThumbOffset}px`,
                } as React.CSSProperties
              }
            >
              <input
                id={inputId}
                type="range"
                min={0}
                max={partySizeOptions.length - 1}
                step={1}
                value={partySizeIndex}
                aria-valuetext={`${partySize}`}
                onChange={handleInputChange}
              />
              <output htmlFor={inputId} aria-live="polite">
                {partySize}
              </output>
            </div>
            <button
              type="button"
              aria-label={`Increase ${inputLabel}`}
              disabled={partySizeIndex === partySizeOptions.length - 1}
              onClick={() => changePartySize(1)}
            >
              +
            </button>
          </div>
        </div>
        {description && <p className={Styles.description}>{description}</p>}
      </div>

      <div className={Styles.resultsContainer}>
        <div className={Styles.categoryNavigationTop}>
          <button
            type="button"
            className={Styles.categoryArrow}
            aria-label="Show previous category above recommendations"
            disabled={activeCategoryIndex === 0}
            onClick={() => changeCategory(-1)}
          >
            <ArrowLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className={Styles.categoryArrow}
            aria-label="Show next category above recommendations"
            disabled={
              activeCategoryIndex ===
              CHICKEN_FINGER_CALCULATOR_CATEGORIES.length - 1
            }
            onClick={() => changeCategory(1)}
          >
            <ArrowRight aria-hidden="true" />
          </button>
        </div>

        <div
          ref={resultsRef}
          className={Styles.results}
          role="group"
          aria-roledescription="carousel"
          aria-label="Order recommendations by category"
          aria-live="polite"
        >
          {CHICKEN_FINGER_CALCULATOR_CATEGORIES.map(category => {
            const categoryRecommendations = recommendations.filter(
              recommendation => recommendation.category === category
            )

            return (
              <div
                className={Styles.category}
                key={category}
                role="group"
                aria-roledescription="slide"
                aria-label={`${CATEGORY_LABELS[category]}, category ${
                  CHICKEN_FINGER_CALCULATOR_CATEGORIES.indexOf(category) + 1
                } of ${CHICKEN_FINGER_CALCULATOR_CATEGORIES.length}`}
              >
                <h3>{CATEGORY_LABELS[category]}</h3>
                {categoryDescriptions[category] && (
                  <p className={Styles.categoryDescription}>
                    {categoryDescriptions[category]}
                  </p>
                )}
                {categoryRecommendations.length > 0 && (
                  <ul className={Styles.recommendations}>
                    {categoryRecommendations.map(recommendation => (
                      <li
                        className={Styles.recommendation}
                        key={`${category}-${recommendation.itemName}`}
                      >
                        <div>
                          <h4>{recommendation.itemName}</h4>
                          {recommendation.itemImage && (
                            <GatsbyImage
                              className={Styles.productImage}
                              image={recommendation.itemImage}
                              alt={recommendation.itemImageAlt ?? ''}
                            />
                          )}
                          <p
                            className={Styles.quantity}
                            key={`${recommendation.itemName}-${recommendation.quantity}`}
                          >
                            {recommendation.quantity}{' '}
                            {recommendation.resultLabel ?? 'recommended'}
                          </p>
                          {recommendation.itemDescription && (
                            <p>{recommendation.itemDescription}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {categoryRecommendations.length === 0 && partySize !== 0 && (
                  <p className={Styles.noResults}>{noResultsMessage}</p>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div
        className={Styles.categoryPagination}
        role="group"
        aria-label="Select a category"
      >
        <button
          type="button"
          className={Styles.categoryArrow}
          aria-label="Show previous category"
          disabled={activeCategoryIndex === 0}
          onClick={() => changeCategory(-1)}
        >
          <ArrowLeft aria-hidden="true" />
        </button>
        {CHICKEN_FINGER_CALCULATOR_CATEGORIES.map((category, categoryIndex) => (
          <button
            key={category}
            type="button"
            className={`${Styles.categoryPaginationButton} ${
              activeCategoryIndex === categoryIndex
                ? Styles.activeCategoryPaginationButton
                : ''
            }`}
            aria-label={`Show ${CATEGORY_LABELS[category]}`}
            aria-current={
              activeCategoryIndex === categoryIndex ? 'true' : undefined
            }
            onClick={() => goToCategory(categoryIndex)}
          />
        ))}
        <button
          type="button"
          className={Styles.categoryArrow}
          aria-label="Show next category"
          disabled={
            activeCategoryIndex ===
            CHICKEN_FINGER_CALCULATOR_CATEGORIES.length - 1
          }
          onClick={() => changeCategory(1)}
        >
          <ArrowRight aria-hidden="true" />
        </button>
      </div>
      {orderCta?.link?.url && orderCta.text && (
        <div className={Styles.orderCta}>
          <CustomLink
            className={
              backgroundColor === ChickenFingerCalculatorBackgroundColor.white
                ? GlobalStyles.redButton
                : GlobalStyles.whiteButton
            }
            link={orderCta.link}
          >
            {orderCta.text}
          </CustomLink>
        </div>
      )}
    </section>
  )
}

export default ChickenFingerCalculator
